import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  centavos,
  formatMoney,
  fxRate,
  hoy,
  newId,
  parseMoney,
  usdToCordobas,
  type Centavos,
} from '@abastece/shared';
import { readSettings } from '../db/reader.js';
import { emitirYVer } from '../lib/emitir.js';
import { useT } from '../i18n/index.js';
import type { Paso } from './Asistente.js';
import { Campo } from './Campo.js';
import { Dato, DatoTexto, enDolares, fechaCorta } from './CuentasDeCaja.js';

/**
 * Money the owner took out of the safe for themselves, a question to a screen: how much of each
 * currency and on what day, what for if they want to say, and a check (`dueno.retiro`). Not an
 * expense: it lowers what the owner has in the business, never the profit. Asked before the safe
 * is counted, or it comes up short by it.
 */

export const PASOS_DEL_DUENO = ['dueno-monto', 'dueno-revise'] as const;

/** An amount as typed: nothing typed is none, anything else must be an amount. */
const leer = (texto: string): Centavos | null => (texto.trim() === '' ? centavos(0) : parseMoney(texto));

export function usePasosDelDueno({ ir, alGuardar }: { ir: (id: string) => void; alGuardar: (movimientoIds: string[]) => void }): {
  pasos: Paso[];
  empezar: () => void;
} {
  const t = useT();
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const [cordobas, setCordobas] = useState('');
  const [dolares, setDolares] = useState('');
  const [dia, setDia] = useState('');
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const c = leer(cordobas);
  const d = leer(dolares);
  const fecha = dia || hoy();
  const valido = c !== null && c >= 0 && d !== null && d >= 0 && c + d > 0 && hoy() >= fecha;

  async function guardar() {
    if (!valido) return;
    setGuardando(true);
    setError(null);
    try {
      // One for each currency: dollars leave general cash at the official rate, what it carries them at.
      const salidas = [
        ...(c! > 0 ? [{ origen: 'efectivo_cordobas' as const, monto: c! }] : []),
        ...(d! > 0 ? [{ origen: 'efectivo_dolares' as const, monto: usdToCordobas(d!, fxRate(config.fxTasaOficial)) }] : []),
      ];
      const ids: string[] = [];
      for (const s of salidas) {
        const movimientoId = newId();
        const { rechazo } = await emitirYVer('dueno.retiro', {
          movimientoId,
          fecha,
          monto: s.monto,
          origen: s.origen,
          ...(motivo.trim() ? { motivo: motivo.trim() } : {}),
        });
        if (rechazo) {
          setError(rechazo);
          return;
        }
        ids.push(movimientoId);
      }
      setCordobas('');
      setDolares('');
      setDia('');
      setMotivo('');
      alGuardar(ids);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setGuardando(false);
    }
  }

  const pasos: Paso[] = [
    {
      id: 'dueno-monto',
      titulo: t('duenoPaso.monto'),
      explicacion: <p>{t('duenoPaso.montoExplicacion')}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <Campo
            etiqueta={t('deposito.cordobas')}
            inputMode="decimal"
            className="tabular text-lg"
            value={cordobas}
            onChange={(e) => setCordobas(e.target.value)}
            placeholder="0.00"
            autoComplete="off"
            error={c === null || c < 0 ? t('caja.montoInvalido') : null}
          />
          <Campo
            etiqueta={t('deposito.dolares')}
            inputMode="decimal"
            className="tabular text-lg"
            value={dolares}
            onChange={(e) => setDolares(e.target.value)}
            placeholder="0.00"
            autoComplete="off"
            error={d === null || d < 0 ? t('caja.montoInvalido') : null}
          />
          <Campo
            etiqueta={t('duenoPaso.fecha')}
            type="date"
            value={fecha}
            max={hoy()}
            onChange={(e) => setDia(e.target.value)}
            error={fecha <= hoy() ? null : t('gastoPaso.fechaFutura')}
          />
          <Campo
            maxLength={200}
            etiqueta={t('duenoPaso.motivo')}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            autoComplete="off"
            ayuda={t('duenoPaso.motivoAyuda')}
          />
        </div>
      ),
      seguir: { etiqueta: t('asistente.siguiente'), deshabilitado: !valido, alTocar: () => ir('dueno-revise') },
    },
    {
      id: 'dueno-revise',
      titulo: t('turno.revise'),
      explicacion: <p>{t('duenoPaso.reviseExplicacion')}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <div className="tarjeta flex flex-col gap-2">
            {(c ?? 0) > 0 && <Dato etiqueta={t('deposito.cordobas')} valor={formatMoney(c!)} />}
            {(d ?? 0) > 0 && <Dato etiqueta={t('deposito.dolares')} valor={enDolares(d!)} />}
            <Dato etiqueta={t('cuadrar.fecha')} valor={fechaCorta(fecha)} />
            {motivo.trim() && <DatoTexto etiqueta={t('duenoPaso.motivo')} valor={motivo.trim()} />}
          </div>
          {error && <p className="text-sm text-rose-400">{error}</p>}
        </div>
      ),
      seguir: { etiqueta: t('duenoPaso.guardar'), icono: 'guardar', deshabilitado: guardando || !valido, alTocar: () => void guardar() },
    },
  ];

  return {
    pasos,
    empezar: () => {
      setError(null);
      ir('dueno-monto');
    },
  };
}
